import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";

// Report type options
const REPORT_TYPES = [
  { id: "bug", label: "Bug / Something not working" },
  { id: "friction", label: "Slows me down / Workflow friction" },
  { id: "suggestion", label: "Improvement suggestion" },
  { id: "logic_concern", label: "Logic concern (JCE / pricing logic)" },
  { id: "usability", label: "Usability problem (layout / mobile)" },
  { id: "performance", label: "Performance issue (slow loading)" },
  { id: "other", label: "Other" }
];

// Disruption levels
const DISRUPTION_LEVELS = [
  { level: 1, label: "1 - Minor annoyance" },
  { level: 2, label: "2 - Slight slowdown" },
  { level: 3, label: "3 - Noticeable friction" },
  { level: 4, label: "4 - Major workflow slowdown" },
  { level: 5, label: "5 - Blocks work completely" }
];

const FeedbackReportButton = ({ 
  currentPage = "unknown", 
  estimateId = null, 
  vehicleReg = null,
  variant = "ghost",
  size = "sm",
  className = ""
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [reportType, setReportType] = useState("");
  const [attemptDescription, setAttemptDescription] = useState("");
  const [outcomeDescription, setOutcomeDescription] = useState("");
  const [disruptionLevel, setDisruptionLevel] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");
  
  const resetForm = () => {
    setReportType("");
    setAttemptDescription("");
    setOutcomeDescription("");
    setDisruptionLevel("");
    setAdditionalNotes("");
    setScreenshotUrl("");
    setSubmitted(false);
  };
  
  const handleSubmit = async () => {
    // Validate required fields
    if (!reportType) {
      toast.error("Please select a report type");
      return;
    }
    if (!attemptDescription.trim()) {
      toast.error("Please describe what you were trying to do");
      return;
    }
    if (!outcomeDescription.trim()) {
      toast.error("Please describe what happened instead");
      return;
    }
    if (!disruptionLevel) {
      toast.error("Please select a disruption level");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Auto-capture browser and device info
      const browserInfo = navigator.userAgent;
      const deviceInfo = `${navigator.platform} | ${window.screen.width}x${window.screen.height}`;
      
      await axios.post(`${API}/feedback/report`, {
        report_type: reportType,
        attempt_description: attemptDescription.trim(),
        outcome_description: outcomeDescription.trim(),
        disruption_level: parseInt(disruptionLevel),
        screenshot_url: screenshotUrl || null,
        additional_notes: additionalNotes.trim() || null,
        current_page: currentPage,
        estimate_id: estimateId,
        vehicle_reg: vehicleReg,
        browser_info: browserInfo,
        device_info: deviceInfo
      });
      
      setSubmitted(true);
      toast.success("Feedback submitted successfully");
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        setOpen(false);
        resetForm();
      }, 2000);
      
    } catch (err) {
      toast.error("Failed to submit feedback", {
        description: err.response?.data?.detail || "Please try again"
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <>
      <Button 
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={`text-slate-500 hover:text-slate-700 ${className}`}
        data-testid="feedback-report-btn"
      >
        <MessageSquareWarning className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Report Issue</span>
      </Button>
      
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            // Success state
            <div className="py-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-700">Thank You!</h3>
              <p className="text-slate-600 mt-2">
                Your feedback has been recorded and will be reviewed.
              </p>
            </div>
          ) : (
            // Form state
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquareWarning className="w-5 h-5 text-amber-500" />
                  Report Issue / Suggest Improvement
                </DialogTitle>
                <DialogDescription>
                  Help us improve by sharing what's not working or could be better.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Report Type - Required */}
                <div>
                  <Label className="font-medium">
                    Report Type <span className="text-red-500">*</span>
                  </Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger className="mt-1" data-testid="report-type-select">
                      <SelectValue placeholder="Select report type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* What were you trying to do? - Required */}
                <div>
                  <Label className="font-medium">
                    What were you trying to do? <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={attemptDescription}
                    onChange={(e) => setAttemptDescription(e.target.value)}
                    placeholder="e.g., Add a part to the pricing..."
                    className="mt-1"
                    data-testid="attempt-input"
                  />
                </div>
                
                {/* What happened instead? - Required */}
                <div>
                  <Label className="font-medium">
                    What happened instead? <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={outcomeDescription}
                    onChange={(e) => setOutcomeDescription(e.target.value)}
                    placeholder="e.g., The save button didn't respond..."
                    className="mt-1"
                    data-testid="outcome-input"
                  />
                </div>
                
                {/* Disruption Level - Required */}
                <div>
                  <Label className="font-medium">
                    Disruption Level <span className="text-red-500">*</span>
                  </Label>
                  <Select value={disruptionLevel} onValueChange={setDisruptionLevel}>
                    <SelectTrigger className="mt-1" data-testid="disruption-select">
                      <SelectValue placeholder="How much did this affect your work?" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISRUPTION_LEVELS.map(level => (
                        <SelectItem key={level.level} value={level.level.toString()}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Additional Notes - Optional */}
                <div>
                  <Label className="font-medium text-slate-600">
                    Additional Notes <span className="text-slate-400">(optional)</span>
                  </Label>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Any extra context or details..."
                    className="mt-1"
                    rows={3}
                    data-testid="notes-textarea"
                  />
                </div>
                
                {/* Screenshot URL - Optional */}
                <div>
                  <Label className="font-medium text-slate-600">
                    Screenshot URL <span className="text-slate-400">(optional)</span>
                  </Label>
                  <Input
                    value={screenshotUrl}
                    onChange={(e) => setScreenshotUrl(e.target.value)}
                    placeholder="Paste a link to a screenshot..."
                    className="mt-1"
                    data-testid="screenshot-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Tip: Use your phone's screenshot feature, upload to a sharing service, and paste the link here.
                  </p>
                </div>
                
                {/* Auto-captured context notice */}
                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                  <p className="font-medium text-slate-600 mb-1">Auto-captured context:</p>
                  <p>Page: {currentPage}</p>
                  {vehicleReg && <p>Vehicle: {vehicleReg}</p>}
                  {estimateId && <p>Estimate: {estimateId.substring(0, 8)}...</p>}
                  <p>User: {user?.name || "Unknown"} ({user?.role || "Unknown"})</p>
                </div>
                
                {/* Submit Button */}
                <Button 
                  className="w-full" 
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? "Submitting..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackReportButton;
